// „Dokumente“ (Screen mieter/05): nur vom Büro freigegebene Unterlagen, nach Gruppen. Download über
// portal-datei (prüft bei jedem Abruf). Ein „Neu“-Merker fehlt: dafür gibt es keine Daten.
import { useState } from 'react';
import { CircleNotch, ClipboardText, DownloadSimple, FileText, FolderSimple, HouseLine, Receipt } from '@phosphor-icons/react';
import { FehlerHinweis, IconChip, LeerZustand, SeitenTitel, farbKlasse, type PortalFarbe } from '../../components/portal/bausteine';
import { dateiKuerzel, dokMeta, gruppiereDokumente, type DokGruppe, type PortalDokument } from '../../lib/portal/mieterE5';
import { ladeDokumentHerunter } from '../../lib/portal/mieterApi';
import type { Icon } from '@phosphor-icons/react';

const GRUPPEN_STIL: Record<DokGruppe, { icon: Icon; farbe: PortalFarbe }> = {
  mietvertrag: { icon: FileText, farbe: 'wohnung' },
  abrechnungen: { icon: Receipt, farbe: 'konto' },
  protokolle: { icon: ClipboardText, farbe: 'schluessel' },
  haus: { icon: HouseLine, farbe: 'gemein' },
  weitere: { icon: FolderSimple, farbe: 'sonst' },
};

export default function MieterDokumente({ dokumente }: { dokumente: PortalDokument[] }) {
  const [laedt, setLaedt] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const gruppen = gruppiereDokumente(dokumente);

  const laden = async (d: PortalDokument) => {
    if (laedt) return;
    setLaedt(d.id); setFehler(null);
    try { await ladeDokumentHerunter(d); } catch (e) { setFehler((e as Error).message); } finally { setLaedt(null); }
  };

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SeitenTitel oben="Von der Verwaltung freigegeben" titel="Dokumente" />
      {fehler && <FehlerHinweis text={fehler} onSchliessen={() => setFehler(null)} />}
      {gruppen.length === 0 ? <LeerZustand icon={FolderSimple} text="Zurzeit sind keine Dokumente für Sie freigegeben" /> : (
        <div style={{ display: 'grid', gap: 14, alignItems: 'start', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 1fr))' }}>
          {gruppen.map((g) => {
            const stil = GRUPPEN_STIL[g.key];
            return (
              <section key={g.key} className={`p-glas p-hebt ${farbKlasse(stil.farbe)}`} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }} aria-label={g.label}>
                <div className="p-glas-schimmer"><stil.icon size={170} weight="duotone" className="p-wasserzeichen" style={{ opacity: 0.11 }} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <IconChip farbe={stil.farbe} icon={stil.icon} />
                  <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, flex: 1 }}>{g.label}</h2>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-w45)' }}>{g.docs.length === 1 ? '1 Dokument' : `${g.docs.length} Dokumente`}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {g.docs.map((d) => (
                    <button key={d.id} type="button" className="p-zeile" disabled={!d.herunterladbar || !!laedt} onClick={() => void laden(d)}
                      data-tip={d.herunterladbar ? `${d.titel} herunterladen` : 'Bitte bei der Verwaltung anfordern'} style={{ padding: '10px 10px' }}>
                      <span aria-hidden style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, background: 'var(--p-fehler-bg)', color: 'var(--p-fehler-text)' }}>{dateiKuerzel(d)}</span>
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{d.titel}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--p-w50)' }}>{d.herunterladbar ? dokMeta(d) : `${dokMeta(d)} · bei der Verwaltung anfordern`}</span>
                      </span>
                      {laedt === d.id ? <CircleNotch size={17} weight="bold" className="animate-spin" /> : d.herunterladbar && <DownloadSimple size={17} weight="bold" style={{ color: 'var(--p-w55)' }} />}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
